import type { Fraction } from '../../util/fractionType';
import type { StructuredLogger } from '@mimir/primitive/structuredLogger';
import { invariant } from '../../util/invariant';
import type { KLV } from './klv';
import { calculateKlvTotalSize, createKlvFill } from './klv';
import { SMPTE_UL_PRIMER_PACK, MxfDictionary, SMPTE_UL_KLV_FILLS, isStructuralMetadataSet } from './mxfDictionary';
import { Parser } from './parser';
import { parsePrimerPack } from './primerPack';
import { ArraySerializerTarget, Serializer } from './serializer';
import { SizeCalculator } from './sizeCalculator';
import type { Metadata, Preface, ContentStorage, MaterialPackage, SourcePackage, TimelineTrack, StaticTrack, Sequence, Component, MultipleDescriptor, SubDescriptor } from './structuralMetadataDefinition';
import { SingleArrayParserSource } from './singleArrayBuffer';
import { safeArrayAccess } from '@mimir/common/util/safeArrayAccess';


export interface StructuralMetadataReserializer {
  /**
   *  This method should parse the KLV and update its internal state
   *
   *  If the parser has finished it should not accept anymore KLVs
   */
  parseKlv(klv: KLV): boolean;

  /**
   *  Conclude the parsing and resolve the metadata structure. The
   *  returned resolved Preface must allow mutating the internal
   *  state of the parser so that any modifications will affect
   *  the reserialization
   */
  finish(): Preface;

  /**
   *  Reserialize the KLVs passed in to parseKlv with any modifications
   *  applied by changes made to the resolved Preface object
   */
  reserialize(kagSize: number): KLV[];
}

export function makeStructuralMetadataReserializer(structuredLogger: StructuredLogger): StructuralMetadataReserializer {
  return new StructuralMetadataReserializeImpl(structuredLogger);
}

// End of public API

// While parsing we need keep track of unresolved parsed Metadata which only
// holds a UID to its children which we can at a later point resolve into
// the metadata tree structure.

export interface StrongRef<_T> {
  readonly uid: string,
}

export interface UnresolvedPreface extends Omit<Preface, 'contentStorageRef'> {
  contentStorageRef: StrongRef<UnresolvedContentStorage>;
}

export interface UnresolvedContentStorage extends Omit<ContentStorage, 'packages'> {
  packages: StrongRef<UnresolvedPackage>[];
}

export interface UnresolvedMaterialPackage extends Omit<MaterialPackage, 'tracks'> {
  tracks: StrongRef<UnresolvedTrack>[];
}

export interface UnresolvedSourcePackage extends Omit<SourcePackage, 'tracks' | 'descriptor'> {
  tracks: StrongRef<UnresolvedTrack>[];
  descriptor: StrongRef<UnresolvedMultipleDescriptor | SubDescriptor>;
}

export type UnresolvedPackage = UnresolvedMaterialPackage | UnresolvedSourcePackage;

export interface UnresolvedTimelineTrack extends Omit<TimelineTrack, 'sequenceRef'> {
  sequenceRef: StrongRef<UnresolvedSequence>;
}

export type UnresolvedTrack = UnresolvedTimelineTrack | StaticTrack;

export interface UnresolvedSequence extends Omit<Sequence, 'structuralComponents'> {
  structuralComponents: StrongRef<Component>[],
}

export interface UnresolvedMultipleDescriptor extends Omit<MultipleDescriptor, 'subDescriptors'> {
  subDescriptors: StrongRef<SubDescriptor>[],
}

export type UnresolvedMetadata =
  UnresolvedPreface |
  UnresolvedContentStorage |
  UnresolvedPackage |
  UnresolvedTrack |
  UnresolvedSequence |
  UnresolvedMultipleDescriptor |
  SubDescriptor |
  Component;

// KLVs that we don't understand at all are treated as completely opaque
interface OpaqueKLV {
  type: 'opaque',
  klv: KLV,
}

// Other KLVs may be partially opaque in that some fields are parsed into
// actual values while others remain as untouched bytes
type UL = string;
type OpaqueMetadata = Record<UL, Uint8Array>;

interface ParsedKLV {
  type: 'parsed',
  ul: string,
  instanceUid: string,
  // The unresolved metadata only contain uid references to children
  unresolved: UnresolvedMetadata,
  // The resolved metadata contain object references to children and
  // forms a object tree. This tree will be built from the unresolved
  // metadata when finish is called.
  resolved: Metadata | null,
  // This contains all the unknown fields that are simply stored in a
  // UL to bytes map.
  opaque: OpaqueMetadata,
}

type MetadataKLV = (ParsedKLV | OpaqueKLV);

class StructuralMetadataReserializeImpl implements StructuralMetadataReserializer {
  private primerPack: KLV | undefined;
  private klvs: MetadataKLV[] = [];
  private dictionary = new MxfDictionary();
  public constructor(private structuredLogger: StructuredLogger) { }

  public parseKlv(klv: KLV): boolean {
    if (SMPTE_UL_KLV_FILLS.has(klv.key)) {
      return true;
    }
    if (klv.key === SMPTE_UL_PRIMER_PACK) {
      if (this.primerPack !== undefined) {
        this.structuredLogger.log({ level: 'warn', message: 'Multiple PrimerPacks encountered' });
      }
      this.primerPack = klv;
      const primerPack = parsePrimerPack(klv.value);
      for (const entry of primerPack) {
        this.dictionary.addLocalTag(entry.localTag, entry.ul);
      }
    } else {
      const name = this.dictionary.getName(klv.key);
      const isKnownKlv = (name !== undefined);
      if (isKnownKlv && !isStructuralMetadataSet(klv.key)) {
        this.structuredLogger.log({ level: 'info', message: 'Known KLV encountered which is not a structural metadata set. Assuming end of header.', key: klv.key, name });

        return false;
      }
      if (this.primerPack === undefined) {
        this.structuredLogger.log({ level: 'warn', message: 'Metadata KLV encountered before PrimerPack' });
      }
      this.klvs.push(parseMetadataKlv(this.structuredLogger, this.dictionary, klv));
    }
    return true;
  }

  public finish(): Preface {
    return resolvePreface(this.klvs);
  }

  public reserialize(kagSize: number): KLV[] {
    const klvs : KLV[] = [];
    if (this.primerPack !== undefined) {
      klvs.push(this.primerPack);
      const primerPackFill = createKlvFill(calculateKlvTotalSize(this.primerPack), kagSize);
      if (primerPackFill) {
        klvs.push(primerPackFill);
      }
    }
    let totalMetadataSize = 0;
    for (const parsedKlv of this.klvs) {
      let outKlv : KLV;
      if (parsedKlv.type === 'opaque') {
        outKlv = parsedKlv.klv;
      } else {
        const target = new ArraySerializerTarget();
        const serializer = new Serializer(target);
        serializeParsedKlv(this.dictionary, serializer, parsedKlv);
        const value = target.getArray();
        outKlv = {
          key: parsedKlv.ul,
          length: {
            bytesUsed: 4,
            length: value.length,
          },
          value,
        };
      }
      totalMetadataSize += calculateKlvTotalSize(outKlv);
      klvs.push(outKlv);
    }
    const endFill = createKlvFill(totalMetadataSize, kagSize);
    if (endFill !== undefined) {
      klvs.push(endFill);
    }
    return klvs;
  }
}

function resolvePreface(klvs: MetadataKLV[]): Preface {
  let preface : Preface | undefined;
  for (const klv of klvs) {
    if (klv.type !== 'parsed') {
      continue;
    }
    if (klv.unresolved.type !== 'preface') {
      continue;
    }
    if (klv.resolved === null) {
      klv.resolved = resolveMetadata(klv.unresolved, klvs);
    }
    invariant(preface === undefined, 'Multiple prefaces found');
    invariant(klv.resolved.type === 'preface', 'Preface change type during resolve');
    preface = klv.resolved;
  }
  invariant(preface !== undefined, 'No Preface found');
  return preface;
}

function resolveMetadata(metadata: UnresolvedMetadata, klvs: MetadataKLV[]): Metadata {
  switch (metadata.type) {
    case 'preface':
      const contentStorageRef = resolveStrongRef(metadata.contentStorageRef, klvs);
      invariant(contentStorageRef.type === 'content_storage', 'Wrong type for ContentStorageRef');
      return { ...metadata, contentStorageRef };
    case 'content_storage':
      return {
        ...metadata,
        packages: metadata.packages.map((packageRef) => {
          const resolved = resolveStrongRef(packageRef, klvs);
          invariant(resolved.type === 'source_package' || resolved.type === 'material_package', 'Wrong type for PackageRef');
          return resolved;
        })
      };
    case 'material_package':
      return {
        ...metadata,
        tracks: metadata.tracks.map((packageRef) => {
          const resolved = resolveStrongRef(packageRef, klvs);
          invariant(resolved.type === 'timeline_track' || resolved.type === 'static_track', 'Wrong type for PackageRef');
          return resolved;
        }),
      };
    case 'source_package':
      const descriptor = resolveStrongRef(metadata.descriptor, klvs);
      invariant(
        (descriptor.type === 'multiple_descriptor') ||
        (descriptor.type === 'generic_picture_essence_descriptor') ||
        (descriptor.type === 'generic_audio_essence_descriptor') ||
        (descriptor.type === 'unknown_essence_descriptor'),
        `Wrong type for MultipleDescriptor: ${descriptor.type}`
      );
      return {
        ...metadata,
        tracks: metadata.tracks.map((packageRef) => {
          const resolved = resolveStrongRef(packageRef, klvs);
          invariant(resolved.type === 'timeline_track' || resolved.type === 'static_track', 'Wrong type for PackageRef');
          return resolved;
        }),
        descriptor,
      };
    case 'static_track':
      return metadata;
    case 'timeline_track': {
      const sequenceRef = resolveStrongRef(metadata.sequenceRef, klvs);
      invariant(sequenceRef.type === 'sequence', 'Wrong type for SequenceRef');
      return { ...metadata, sequenceRef };
    }
    case 'sequence':
      return {
        ...metadata,
        structuralComponents: metadata.structuralComponents.map((packageRef) => {
          const resolved = resolveStrongRef(packageRef, klvs);
          invariant(resolved.type === 'timecode_component' || resolved.type === 'source_clip', 'Wrong type for PackageRef');
          return resolved;
        })
      };
    case 'source_clip':
    case 'timecode_component':
      return metadata;
    case 'multiple_descriptor':
      return {
        ...metadata,
        subDescriptors: metadata.subDescriptors.map((fileDescriptor) => {
          const resolved = resolveStrongRef(fileDescriptor, klvs, { type: 'unknown_essence_descriptor' });
          invariant(
            resolved.type === 'generic_picture_essence_descriptor' ||
            resolved.type === 'generic_audio_essence_descriptor' ||
            resolved.type === 'unknown_essence_descriptor',
            'Wrong type for SubdescriptorRef'
          );
          return resolved;
        }),
      };
    case 'unknown_essence_descriptor':
    case 'generic_picture_essence_descriptor':
    case 'generic_audio_essence_descriptor':
      return metadata;
  }
}

function resolveStrongRef(
  strongRef: StrongRef<UnresolvedMetadata>,
  klvs: MetadataKLV[],
  fallback?: Metadata
): Metadata {
  const instanceUid = strongRef.uid;
  const matches = klvs.filter((k) => k.type === 'parsed' && k.instanceUid === instanceUid);
  if ((matches.length === 0) && (fallback !== undefined)) {
    return fallback;
  }
  invariant(matches.length > 0, `InstanceUID ${instanceUid} not found.`);
  invariant(matches.length < 2, `Multiple metadata with InstanceUID ${instanceUid} found.`);
  const klv = safeArrayAccess(matches, 0);
  invariant(klv.type === 'parsed', `InstanceUID ${instanceUid} is of unknown type.`);

  if (klv.resolved === null) {
    // Make sure we mutate the internal state of the parser so that the returned
    // object and the internal object is the same object.
    klv.resolved = resolveMetadata(klv.unresolved, klvs);
  }
  return klv.resolved;
}

function parseOpaqueMetadata(dictionary: MxfDictionary, bytes: Uint8Array): OpaqueMetadata {
  const parser = new Parser(new SingleArrayParserSource(bytes));
  const opaque : OpaqueMetadata = { };
  while (parser.remain() > 0) {
    const localTag = parser.readLocalTag();
    const ul = dictionary.getUlFromLocalTag(localTag);
    if (ul === undefined) {
      throw new Error(`Local tag ${localTag} not defined in PrimerPack`);
    }
    const size = parser.readUInt16BE();
    const valueBytes = parser.readUint8Array(size);
    opaque[ul] = valueBytes;
  }
  return opaque;
}

function parseList<T>(parser: Parser, f: (p: Parser) => T): T[] {
  const count = parser.readUInt32BE();
  const elementSize = parser.readUInt32BE();
  const elements : T[] = [];
  for (let i = 0; i < count; i++) {
    const before = parser.remain();
    elements.push(f(parser));
    const after = parser.remain();
    invariant(before - after === elementSize, 'Incorrect element size');
  }
  return elements;
}

function ulFromName(dictionary: MxfDictionary, name: string): string {
  const ul = dictionary.getUl(name);
  invariant(ul !== undefined, `Unknown Metadata type: ${name}`);
  return ul;
}

function mandatory<T>(parser: (fieldName: string) => T | undefined): ((fieldName: string) => T) {
  return (fieldName: string) => {
    const result = parser(fieldName);
    invariant(result !== undefined, `Missing mandatory Metadata field: ${fieldName}`);
    return result;
  };
}

function parseMetadata(
  name: string,
  dictionary: MxfDictionary,
  opaque: OpaqueMetadata
): UnresolvedMetadata | 'unknown' {
  const getOptionalParser = (fieldName: string): Parser | undefined => {
    const ul = ulFromName(dictionary, fieldName);
    const bytes = opaque[ul];
    if (bytes === undefined) {
      return undefined;
    }
    return new Parser(new SingleArrayParserSource(bytes));
  };

  const getParser = (fieldName: string): Parser => {
    const parser = getOptionalParser(fieldName);
    invariant(parser !== undefined, `Missing mandatory Metadata field: ${fieldName}`);
    return parser;
  };

  const parseUid = (fieldName: string) =>
    getParser(fieldName).readUuid();

  const parseUids = (fieldName: string) =>
    parseList(getParser(fieldName), (p) => p.readUuid());

  const parseInt64BE = (fieldName: string): number =>
    getParser(fieldName).readInt64BE();

  const parseUInt64BE = (fieldName: string): number =>
    getParser(fieldName).readUInt64BE();

  const parseUInt32BE = (fieldName: string): number =>
    getParser(fieldName).readUInt32BE();

  const parseUInt16BE = (fieldName: string): number =>
    getParser(fieldName).readUInt16BE();

  const parseUInt8 = (fieldName: string): number =>
    getParser(fieldName).readUInt8();

  const parseUl = (fieldName: string): string =>
    getParser(fieldName).readUl();

  const parseOptionalInt64BE = (fieldName: string): number | undefined =>
    getOptionalParser(fieldName)?.readInt64BE();

  const parseOptionalRational = (fieldName: string): Fraction | undefined =>
    getOptionalParser(fieldName)?.readFraction();

  const parseTrackNumber = (fieldName: string): string =>
    getParser(fieldName).readHex('b.b.b.b');

  const parseRational = mandatory(parseOptionalRational);

  switch (name) {
    case 'Preface':
      return {
        type: 'preface',
        contentStorageRef: { uid: parseUid('ContentStorageRef') },
      };
    case 'ContentStorage':
      return {
        type: 'content_storage',
        packages: parseUids('Packages').map((uid) => ({ uid })),
      };
    case 'MaterialPackage':
      return {
        type: 'material_package',
        tracks: parseUids('Tracks').map((uid) => ({ uid })),
      };
    case 'SourcePackage':
      return {
        type: 'source_package',
        tracks: parseUids('Tracks').map((uid) => ({ uid })),
        descriptor: { uid: parseUid('Descriptor') },
      };
    case 'TimelineTrack':
      return {
        type: 'timeline_track',
        editRate: parseRational('EditRate'),
        sequenceRef: { uid: parseUid('SequenceRef') },
        origin: parseUInt64BE('Origin'),
        trackId: parseUInt32BE('TrackID'),
        trackNumber: parseTrackNumber('TrackNumber'),
      };
    case 'MPEG2PictureEssenceDescriptor':
    case 'CDCIPictureEssenceDescriptor':
      return {
        type: 'generic_picture_essence_descriptor',
        essenceContainer: parseUl('EssenceContainer'),
        pictureEssenceCoding: parseUl('PictureEssenceCoding'),
        linkedTrackId: parseUInt32BE('LinkedTrackID'),
        containerDuration: parseOptionalInt64BE('ContainerDuration'),
      };
    case 'AESAudioEssenceDescriptor':
    case 'WaveAudioEssenceDescriptor':
      return {
        type: 'generic_audio_essence_descriptor',
        essenceContainer: parseUl('EssenceContainer'),
        sampleRate: parseRational('SampleRate'),
        channelCount: parseUInt32BE('ChannelCount'),
        quantizationBits: parseUInt32BE('QuantizationBits'),
        linkedTrackId: parseUInt32BE('LinkedTrackID'),
        containerDuration: parseOptionalInt64BE('ContainerDuration'),
      };
    case 'ANCPacketsEssenceDescriptor':
      return {
        type: 'unknown_essence_descriptor',
      };
    case 'StaticTrack':
      return { type: 'static_track' };
    case 'MultipleDescriptor':
      return {
        type: 'multiple_descriptor',
        subDescriptors: parseUids('SubDescriptors').map((uid) => ({ uid })),
      };
    case 'Sequence':
      return {
        type: 'sequence',
        structuralComponents: parseUids('StructuralComponents').map((uid) => ({ uid })),
        duration: parseOptionalInt64BE('Duration'),
      };
    case 'TimecodeComponent':
      return {
        type: 'timecode_component',
        duration: parseOptionalInt64BE('Duration'),
        roundedTimecodeBase: parseUInt16BE('RoundedTimecodeBase'),
        startTimecode: parseInt64BE('StartTimecode'),
        dropFrame: parseUInt8('DropFrame') != 0,
      };
    case 'SourceClip':
      return {
        type: 'source_clip',
        duration: parseOptionalInt64BE('Duration'),
      };
    default:
      return 'unknown';
  }
}

function parseMetadataKlv(
  structuredLogger: StructuredLogger,
  dictionary: MxfDictionary,
  klv: KLV
): MetadataKLV {
  const name = dictionary.getName(klv.key);
  if (name === undefined) {
    structuredLogger.log({ level: 'warn', message: `Unknown SMPTE UL in headers: ${klv.key}` });
    return { type: 'opaque', klv };
  } else {
    const opaque : OpaqueMetadata =
      isStructuralMetadataSet(klv.key) ? parseOpaqueMetadata(dictionary, klv.value) : { };

    const instanceUidUl = ulFromName(dictionary, 'InstanceUID');
    const instanceUidBytes = opaque[instanceUidUl];
    const instanceUid = instanceUidBytes ? (new Parser(new SingleArrayParserSource(opaque[instanceUidUl]!))).readUuid() : undefined;
    const unresolved = parseMetadata(name, dictionary, opaque);
    if (unresolved === 'unknown') {
      structuredLogger.log({ level: 'warn', message: `Known but ignored SMPTE UL in headers: ${name} (${klv.key}, InstanceUID=${instanceUid})` });
      return { type: 'opaque', klv };
    }

    invariant(instanceUid !== undefined, 'Metadata KLV is missing InstanceUID');
    return {
      type: 'parsed',
      ul: klv.key,
      instanceUid,
      unresolved,
      resolved: null,
      opaque,
    };
  }
}

type FieldWriter = (serializer: Serializer) => void;

const writeOptionalDuration = (duration: number | undefined): FieldWriter | null =>
  (duration === undefined) ? null : (s) => s.writeInt64BE(duration);

// Returns a map from UL to a field writer function for a Metadata object. For optional values this function
// will return null instead a FieldWriter. This map will be used by the serializer to reserialize the metadata
// and preserve the original order of the metadata.
function getFieldWriters(metadata: UnresolvedMetadata | Metadata | 'unknown'): Record<string, FieldWriter | null> {
  // Unknown KLVs are fully opaque
  if (metadata === 'unknown') {
    return { };
  }

  switch (metadata.type) {
    // Note: We don't support mutating the StrongRefs. Because of this
    // there are not many field writers actually needed:
    case 'preface':
    case 'content_storage':
    case 'material_package':
    case 'source_package':
    case 'static_track':
    case 'multiple_descriptor':
    case 'unknown_essence_descriptor':
      return { };
    case 'timeline_track': {
      return {
        Origin: (s) => s.writeUInt64BE(metadata.origin),
        TrackID: (s) => s.writeUInt32BE(metadata.trackId),
        TrackNumber: (s) => s.writeHex(metadata.trackNumber),
        EditRate: (s) => s.writeFraction(metadata.editRate),
      };
    }
    case 'sequence': {
      return {
        Duration: writeOptionalDuration(metadata.duration),
      };
    }
    case 'timecode_component': {
      return {
        Duration: writeOptionalDuration(metadata.duration),
        StartTimecode: (s) => s.writeInt64BE(metadata.startTimecode),
        RoundedTimecodeBase: (s) => s.writeUInt16BE(metadata.roundedTimecodeBase),
        DropFrame: (s) => s.writeUInt8(metadata.dropFrame ? 0x1 : 0x0),
      };
    }
    case 'source_clip': {
      return {
        Duration: writeOptionalDuration(metadata.duration),
      };
    }
    case 'generic_picture_essence_descriptor': {
      return {
        PictureEssenceCoding: (s) => s.writeUl(metadata.pictureEssenceCoding),
        LinkedTrackID: (s) => s.writeUInt32BE(metadata.linkedTrackId),
        ContainerDuration: writeOptionalDuration(metadata.containerDuration),
      };
    }
    case 'generic_audio_essence_descriptor': {
      return {
        EssenceContainer: (s) => s.writeUl(metadata.essenceContainer),
        SampleRate: (s) => s.writeFraction(metadata.sampleRate),
        ChannelCount: (s) => s.writeUInt32BE(metadata.channelCount),
        QuantizationBits: (s) => s.writeUInt32BE(metadata.quantizationBits),
        LinkedTrackID: (s) => s.writeUInt32BE(metadata.linkedTrackId),
        ContainerDuration: writeOptionalDuration(metadata.containerDuration),
      };
    }
  }
}

function serializeParsedKlv(
  dictionary: MxfDictionary,
  serializer: Serializer,
  metadata: ParsedKLV,
) {
  const sizeCalculator = new SizeCalculator();
  const sizeSerializer = new Serializer(sizeCalculator);
  const fieldWriters = getFieldWriters(metadata.resolved ?? metadata.unresolved);
  for (const ul in metadata.opaque) {
    const name = dictionary.getName(ul);
    const fieldWriter = (name !== undefined) ? fieldWriters[name] : undefined;
    if (fieldWriter === null) {
      // Optional field is not set. Explicitly do not write opaque data
      continue;
    }
    const localTag = dictionary.getLocalTagFromUl(ul);
    invariant(localTag !== undefined, `Missing ${ul} from Preface`);

    if (fieldWriter === undefined) {
      // Handle opaque field
      const opaqueValue = metadata.opaque[ul]!;
      serializer.writeLocalTag(localTag);
      serializer.writeUInt16BE(opaqueValue.length);
      serializer.writeBytes(opaqueValue);
    } else {
      sizeCalculator.reset();
      fieldWriter(sizeSerializer);
      const valueSize = sizeCalculator.size();
      serializer.writeLocalTag(localTag);
      serializer.writeUInt16BE(valueSize);
      fieldWriter(serializer);
    }
  }
}


